import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Upload, 
  Image as ImageIcon, 
  Save, 
  Send,
  Video,
  Clock,
  DollarSign,
  BookOpen
} from 'lucide-react';
import { PricingGuideTooltip, PriceWarning } from '@/components/instructor/PricingGuideTooltip';
import { PRICING_GUIDE } from '@/config/pricingGuide';

// Use relative path for API calls to avoid CORS issues
const API = '/api';

const CourseBuilder = () => {
  const navigate = useNavigate();
  const { id: courseId } = useParams();
  const isEditMode = !!courseId;
  
  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [videoUploadProgress, setVideoUploadProgress] = useState(0);
  
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    description: '',
    category: 'pilates',
    level: 'beginner',
    lesson_type: 'recorded',  // live | recorded | live_plus_recorded
    live_date: '',
    live_time: '',
    live_stream_url: '',
    max_participants: '',  // Kontenjan - canlı dersler için
    recording_url: '',
    recording_available: false,
    duration: '',
    price: '',
    thumbnail_url: '',
    access_type: 'unlimited',
    status: 'draft'
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isEditMode) {
      fetchCourse();
    }
  }, [courseId]);

  const fetchCourse = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await axios.get(`${API}/instructor/courses/${courseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const course = response.data;
      setFormData({
        title: course.title || '',
        subtitle: course.subtitle || '',
        description: course.description || '',
        category: course.category || 'pilates',
        level: course.level || 'beginner',
        lesson_type: course.lesson_type || 'recorded',
        live_date: course.live_date || '',
        live_time: course.live_time || '',
        live_stream_url: course.live_stream_url || '',
        max_participants: course.max_participants?.toString() || '',
        recording_url: course.recording_url || course.video_url || '',
        recording_available: course.recording_available || false,
        duration: course.duration?.toString() || '',
        price: course.price?.toString() || '',
        thumbnail_url: course.thumbnail_url || '',
        access_type: course.access_type || 'unlimited',
        status: course.status || 'draft'
      });
    } catch (error) {
      console.error('Error:', error);
      if (error.response?.status === 403) {
        toast.error('Bu kursa erişim yetkiniz yok');
        navigate('/instructor/dashboard');
      } else if (error.response?.status === 404) {
        toast.error('Kurs bulunamadı');
        navigate('/instructor/dashboard');
      } else {
        toast.error('Kurs yüklenirken hata oluştu');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleThumbnailUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Sadece JPG, PNG veya WebP dosyaları yüklenebilir');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Dosya boyutu 5MB\'dan küçük olmalıdır');
      return;
    }

    setUploadingThumbnail(true);
    try {
      const token = localStorage.getItem('token');
      const uploadData = new FormData();
      uploadData.append('file', file);

      const response = await axios.post(`${API}/upload/thumbnail`, uploadData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setFormData({ ...formData, thumbnail_url: response.data.file_url });
      toast.success('Görsel yüklendi');
    } catch (error) {
      toast.error('Görsel yüklenirken hata oluştu');
    } finally {
      setUploadingThumbnail(false);
    }
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['video/mp4', 'video/webm', 'video/quicktime', 'video/x-msvideo'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Sadece MP4, WebM, MOV veya AVI dosyaları yüklenebilir');
      return;
    }

    if (file.size > 500 * 1024 * 1024) {
      toast.error('Video boyutu 500MB\'dan küçük olmalıdır');
      return;
    }

    setUploadingVideo(true);
    setVideoUploadProgress(0);
    
    try {
      const token = localStorage.getItem('token');
      const uploadData = new FormData();
      uploadData.append('file', file);

      const response = await axios.post(`${API}/upload/video`, uploadData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setVideoUploadProgress(progress);
        }
      });

      setFormData({ ...formData, video_url: response.data.file_url });
      toast.success(`Video yüklendi (${response.data.size_mb} MB)`);
    } catch (error) {
      console.error('Video upload error:', error);
      toast.error(error.response?.data?.detail || 'Video yüklenirken hata oluştu');
    } finally {
      setUploadingVideo(false);
      setVideoUploadProgress(0);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Kurs başlığı zorunludur';
    }
    if (!formData.description.trim()) {
      newErrors.description = 'Kurs açıklaması zorunludur';
    }
    if (!formData.duration || parseInt(formData.duration) <= 0) {
      newErrors.duration = 'Geçerli bir süre girin';
    }
    if (!formData.price || parseFloat(formData.price) < 0) {
      newErrors.price = 'Geçerli bir fiyat girin';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e, publishAfterSave = false) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Lütfen zorunlu alanları doldurun');
      return;
    }

    setSaving(true);

    try {
      const token = localStorage.getItem('token');
      
      const submitData = {
        ...formData,
        duration: parseInt(formData.duration),
        price: parseFloat(formData.price),
        max_participants: formData.max_participants ? parseInt(formData.max_participants) : null,
        status: publishAfterSave ? 'published' : formData.status
      };

      if (isEditMode) {
        await axios.put(`${API}/instructor/courses/${courseId}`, submitData, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        toast.success(publishAfterSave ? 'Kurs güncellendi ve yayınlandı!' : 'Kurs güncellendi!');
      } else {
        await axios.post(`${API}/instructor/courses`, submitData, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        toast.success(publishAfterSave ? 'Kurs oluşturuldu ve yayınlandı!' : 'Kurs taslak olarak kaydedildi!');
      }

      navigate('/instructor/dashboard');
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Kurs kaydedilirken hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 wellness-gradient flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 wellness-gradient">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        
        {/* Back Button */}
        <Button 
          variant="ghost" 
          className="mb-6"
          onClick={() => navigate('/instructor/dashboard')}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Dashboard&apos;a Dön
        </Button>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold">
            {isEditMode ? 'Kursu Düzenle' : 'Yeni Kurs Oluştur'}
          </h1>
          <p className="text-gray-600 mt-2">
            {isEditMode 
              ? 'Kurs bilgilerini güncelleyin' 
              : 'Yeni bir kurs oluşturun ve öğrencilerinize ulaşın'}
          </p>
        </div>

        <form onSubmit={(e) => handleSubmit(e, false)}>
          <div className="space-y-6">
            
            {/* Basic Info Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BookOpen className="h-5 w-5" />
                  Temel Bilgiler
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Title */}
                <div>
                  <Label htmlFor="title">Kurs Başlığı *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Örn: Başlangıç Pilates Programı"
                    className={errors.title ? 'border-red-500' : ''}
                  />
                  {errors.title && <p className="text-red-500 text-sm mt-1">{errors.title}</p>}
                </div>

                {/* Subtitle */}
                <div>
                  <Label htmlFor="subtitle">Alt Başlık (Opsiyonel)</Label>
                  <Input
                    id="subtitle"
                    value={formData.subtitle}
                    onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                    placeholder="Örn: 4 Haftalık Kapsamlı Program"
                  />
                </div>

                {/* Description */}
                <div>
                  <Label htmlFor="description">Açıklama *</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={5}
                    placeholder="Kursunuzun içeriğini, hedef kitlesini ve öğrencilerin neler öğreneceğini açıklayın..."
                    className={errors.description ? 'border-red-500' : ''}
                  />
                  {errors.description && <p className="text-red-500 text-sm mt-1">{errors.description}</p>}
                </div>

                {/* Category & Level */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Kategori *</Label>
                    <Select
                      value={formData.category}
                      onValueChange={(value) => setFormData({ ...formData, category: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pilates">Pilates</SelectItem>
                        <SelectItem value="yoga">Yoga</SelectItem>
                        <SelectItem value="fitness">Fitness</SelectItem>
                        <SelectItem value="meditation">Meditasyon</SelectItem>
                        <SelectItem value="breathing">Nefes</SelectItem>
                        <SelectItem value="mobility">Mobilite</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Seviye *</Label>
                    <Select
                      value={formData.level}
                      onValueChange={(value) => setFormData({ ...formData, level: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="beginner">Başlangıç</SelectItem>
                        <SelectItem value="intermediate">Orta</SelectItem>
                        <SelectItem value="advanced">İleri</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Pricing & Duration Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5" />
                  Fiyat ve Süre
                </CardTitle>
                <CardDescription>
                  Fiyatlandırma önerilerimiz piyasa araştırmasına dayanmaktadır
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="flex items-center">
                      <Label htmlFor="price">Fiyat (TL) *</Label>
                      <PricingGuideTooltip 
                        category={formData.category} 
                        lessonType={formData.lesson_type} 
                      />
                    </div>
                    <Input
                      id="price"
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                      placeholder={
                        PRICING_GUIDE[formData.lesson_type]?.[formData.category]?.recommended 
                          ? `Önerilen: ${PRICING_GUIDE[formData.lesson_type][formData.category].recommended}₺`
                          : "199.90"
                      }
                      className={errors.price ? 'border-red-500' : ''}
                    />
                    {errors.price && <p className="text-red-500 text-sm mt-1">{errors.price}</p>}
                    <PriceWarning 
                      price={formData.price} 
                      category={formData.category} 
                      lessonType={formData.lesson_type} 
                    />
                  </div>

                  <div>
                    <Label htmlFor="duration">Süre (dakika) *</Label>
                    <Input
                      id="duration"
                      type="number"
                      min="1"
                      value={formData.duration}
                      onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                      placeholder="45"
                      className={errors.duration ? 'border-red-500' : ''}
                    />
                    {errors.duration && <p className="text-red-500 text-sm mt-1">{errors.duration}</p>}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Lesson Type & Live Settings Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Video className="h-5 w-5" />
                  Ders Türü ve Yayın Ayarları
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Lesson Type */}
                <div>
                  <Label>Ders Türü *</Label>
                  <Select
                    value={formData.lesson_type}
                    onValueChange={(value) => setFormData({ ...formData, lesson_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="recorded">📹 Kayıtlı Ders - Hazır video içeriği</SelectItem>
                      <SelectItem value="live">🔴 Canlı Ders - Tek seferlik canlı yayın</SelectItem>
                      <SelectItem value="live_plus_recorded">🎬 Canlı + Kayıt - Canlı ders, sonra kayıt erişimi</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-sm text-gray-500 mt-1">
                    {formData.lesson_type === 'recorded' && 'Önceden kaydedilmiş video içeriği. Öğrenciler istedikleri zaman izleyebilir.'}
                    {formData.lesson_type === 'live' && 'Belirli bir tarih ve saatte canlı yayın yapılır.'}
                    {formData.lesson_type === 'live_plus_recorded' && 'Canlı ders yapılır, sonra kayıt olarak satışa devam eder. (Önerilen)'}
                  </p>
                </div>

                {/* Live Settings - Only show for live lessons */}
                {(formData.lesson_type === 'live' || formData.lesson_type === 'live_plus_recorded') && (
                  <div className="border rounded-lg p-4 bg-red-50 space-y-4">
                    <h4 className="font-medium text-red-800">🔴 Canlı Ders Ayarları</h4>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="live_date">Canlı Ders Tarihi *</Label>
                        <Input
                          id="live_date"
                          type="date"
                          value={formData.live_date}
                          onChange={(e) => setFormData({ ...formData, live_date: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label htmlFor="live_time">Başlangıç Saati *</Label>
                        <Input
                          id="live_time"
                          type="time"
                          value={formData.live_time}
                          onChange={(e) => setFormData({ ...formData, live_time: e.target.value })}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="max_participants">Kontenjan (Opsiyonel)</Label>
                        <Input
                          id="max_participants"
                          type="number"
                          min="1"
                          value={formData.max_participants}
                          onChange={(e) => setFormData({ ...formData, max_participants: e.target.value })}
                          placeholder="Örn: 20"
                        />
                        <p className="text-sm text-gray-500 mt-1">
                          Maksimum katılımcı sayısı. Boş bırakılırsa sınırsız.
                        </p>
                      </div>
                      <div>
                        <Label htmlFor="live_stream_url">Canlı Yayın Linki *</Label>
                        <Input
                          id="live_stream_url"
                          value={formData.live_stream_url}
                          onChange={(e) => setFormData({ ...formData, live_stream_url: e.target.value })}
                          placeholder="https://zoom.us/j/... veya meet.google.com/..."
                        />
                      </div>
                    </div>
                    <p className="text-sm text-gray-500">
                      Zoom/Meet linki sadece dersi satın alan öğrencilere gösterilir. Ders başlangıcından 15 dk önce aktif olur.
                    </p>
                  </div>
                )}

                {/* Recording Settings */}
                <div className="border rounded-lg p-4 bg-blue-50 space-y-4">
                  <h4 className="font-medium text-blue-800">📹 Kayıt Ayarları</h4>
                  
                  <div>
                    <Label htmlFor="recording_url">Kayıt Video Linki (Vimeo Embed URL)</Label>
                    <Input
                      id="recording_url"
                      value={formData.recording_url}
                      onChange={(e) => setFormData({ ...formData, recording_url: e.target.value })}
                      placeholder="https://player.vimeo.com/video/123456789"
                    />
                    <p className="text-sm text-gray-500 mt-1">
                      Vimeo&apos;dan &ldquo;Embed&rdquo; linkini alın. Bu link sadece dersi satın alanlara gösterilir.
                    </p>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Switch
                      id="recording_available"
                      checked={formData.recording_available}
                      onCheckedChange={(checked) => setFormData({ ...formData, recording_available: checked })}
                    />
                    <Label htmlFor="recording_available">Kayıt Yayında</Label>
                  </div>
                  <p className="text-sm text-gray-500">
                    {formData.lesson_type === 'live_plus_recorded' 
                      ? 'Canlı ders bittikten sonra kaydı yayınlamak için bu seçeneği açın.' 
                      : 'Kayıt hazır olduğunda bu seçeneği açın.'}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Media Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ImageIcon className="h-5 w-5" />
                  Kapak Görseli
                </CardTitle>
                <CardDescription>
                  Kursunuz için kapak görseli ekleyin
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                
                {/* Thumbnail */}
                <div>
                  <Label>Kapak Görseli</Label>
                  <div className="mt-2">
                    {formData.thumbnail_url ? (
                      <div className="relative inline-block">
                        <img
                          src={`${API}${formData.thumbnail_url}`}
                          alt="Thumbnail"
                          className="w-48 h-32 object-cover rounded-lg border"
                        />
                        <label className="absolute bottom-2 right-2 cursor-pointer bg-white rounded-full p-2 shadow-md hover:bg-gray-100">
                          <Upload className="h-4 w-4" />
                          <input
                            type="file"
                            className="hidden"
                            accept=".jpg,.jpeg,.png,.webp"
                            onChange={handleThumbnailUpload}
                            disabled={uploadingThumbnail}
                          />
                        </label>
                      </div>
                    ) : (
                      <label className="flex flex-col items-center justify-center w-48 h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-purple-400 bg-gray-50">
                        {uploadingThumbnail ? (
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                        ) : (
                          <>
                            <ImageIcon className="h-8 w-8 text-gray-400 mb-2" />
                            <span className="text-sm text-gray-500">Görsel Yükle</span>
                            <span className="text-xs text-gray-400">JPG, PNG, WebP (Max 5MB)</span>
                          </>
                        )}
                        <input
                          type="file"
                          className="hidden"
                          accept=".jpg,.jpeg,.png,.webp"
                          onChange={handleThumbnailUpload}
                          disabled={uploadingThumbnail}
                        />
                      </label>
                    )}
                  </div>
                </div>

                {/* Video Upload */}
                <div>
                  <Label>Kurs Videosu</Label>
                  <div className="mt-2">
                    {formData.video_url ? (
                      <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <Video className="h-6 w-6 text-green-600 mr-3" />
                            <div>
                              <p className="font-medium text-green-800">Video Yüklendi</p>
                              <p className="text-sm text-green-600 truncate max-w-xs">{formData.video_url}</p>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <label className="cursor-pointer">
                              <Button type="button" variant="outline" size="sm" asChild>
                                <span>
                                  <Upload className="h-4 w-4 mr-1" />
                                  Değiştir
                                </span>
                              </Button>
                              <input
                                type="file"
                                className="hidden"
                                accept=".mp4,.webm,.mov,.avi"
                                onChange={handleVideoUpload}
                                disabled={uploadingVideo}
                              />
                            </label>
                            <Button 
                              type="button" 
                              variant="ghost" 
                              size="sm"
                              onClick={() => setFormData({ ...formData, video_url: '' })}
                            >
                              Kaldır
                            </Button>
                          </div>
                        </div>
                      </div>
                    ) : uploadingVideo ? (
                      <div className="p-6 bg-purple-50 rounded-lg border border-purple-200">
                        <div className="flex flex-col items-center">
                          <div className="w-full bg-gray-200 rounded-full h-3 mb-3">
                            <div 
                              className="bg-purple-600 h-3 rounded-full transition-all duration-300" 
                              style={{ width: `${videoUploadProgress}%` }}
                            ></div>
                          </div>
                          <p className="text-sm text-purple-700">
                            Video yükleniyor... {videoUploadProgress}%
                          </p>
                        </div>
                      </div>
                    ) : (
                      <label className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-purple-400 bg-gray-50 transition-colors">
                        <Video className="h-10 w-10 text-gray-400 mb-3" />
                        <span className="text-sm font-medium text-gray-600">Video Yükle</span>
                        <span className="text-xs text-gray-400 mt-1">MP4, WebM, MOV, AVI (Max 500MB)</span>
                        <input
                          type="file"
                          className="hidden"
                          accept=".mp4,.webm,.mov,.avi"
                          onChange={handleVideoUpload}
                          disabled={uploadingVideo}
                        />
                      </label>
                    )}
                  </div>
                  
                  {/* External URL Option */}
                  <div className="mt-4">
                    <details className="group">
                      <summary className="text-sm text-gray-500 cursor-pointer hover:text-purple-600">
                        Veya harici video URL&apos;si girin (YouTube, Vimeo)
                      </summary>
                      <div className="mt-2">
                        <Input
                          value={formData.video_url?.startsWith('/uploads') ? '' : formData.video_url}
                          onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
                          placeholder="https://youtube.com/watch?v=... veya https://vimeo.com/..."
                        />
                      </div>
                    </details>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Status Card */}
            <Card>
              <CardHeader>
                <CardTitle>Yayın Durumu</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-base">Kursu Yayınla</Label>
                    <p className="text-sm text-gray-500">
                      {formData.status === 'published' 
                        ? 'Kurs şu anda yayında ve satışta' 
                        : 'Kurs taslak olarak kaydedilecek'}
                    </p>
                  </div>
                  <Switch
                    checked={formData.status === 'published'}
                    onCheckedChange={(checked) => 
                      setFormData({ ...formData, status: checked ? 'published' : 'draft' })
                    }
                  />
                </div>
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <div className="flex justify-between items-center pt-4">
              <Button 
                type="button" 
                variant="outline"
                onClick={() => navigate('/instructor/dashboard')}
              >
                İptal
              </Button>
              
              <div className="flex gap-3">
                <Button
                  type="submit"
                  variant="outline"
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                      Kaydediliyor...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Taslak Olarak Kaydet
                    </>
                  )}
                </Button>

                <Button
                  type="button"
                  className="btn-primary"
                  disabled={saving}
                  onClick={(e) => handleSubmit(e, true)}
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Yayınlanıyor...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      {isEditMode ? 'Güncelle ve Yayınla' : 'Oluştur ve Yayınla'}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CourseBuilder;
